import { ImageInputProps, Labeled, useInput } from 'react-admin';
import { useInputWithPolicies } from './useInputWithPolicies';
import { IAccessLevel } from '../../shared/utils/policyCheck';
import React, { useRef, useState } from 'react';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useFormContext } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { centerCrop, Crop, makeAspectCrop, ReactCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { LLButton } from '@/components-library';
import { AddAPhoto } from '@mui/icons-material';
import { uniqueId } from 'lodash';
import { enqueueSnackbar } from 'notistack';

export type CommonImageInputProps = {
  value?: string;
  valueSource?: string;
  ignorePolicies?: boolean;
  onDelete?: () => void;
  inputText?: string | React.ReactElement;
  inputBackground?: string;
  isLoading?: boolean;
} & ImageInputProps &
  IAccessLevel;

const CommonImageInput = (props: CommonImageInputProps) => {
  const {
    defaultValue,
    format,
    parse,
    resource,
    source,
    validate,
    onBlur,
    onChange: propsOnChange,

    accept,
    children,
    className,
    helperText,
    inputProps: inputPropsOptions,
    maxSize,
    minSize,
    multiple = false,
    label,
    labelMultiple = 'ra.input.file.upload_several',
    labelSingle = 'ra.input.file.upload_single',
    options = {},
    onRemove: onRemoveProp,
    placeholder,
    removeIcon,
    validateFileRemoval,
    ...rest
  } = props;
  const { onDrop: onDropProp } = options;

  const {
    id,
    field: { name, onChange, value, ...restInput },
    fieldState: { isTouched, error, invalid },
    formState: { isSubmitted },
    isRequired,
  } = useInput({
    resource,
    source,
    type: 'file',
    onChange: composeFile,
  });
  const { watch, setValue } = useFormContext();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const background =
    value?.src ||
    props.value ||
    watch(props.source) ||
    (props.valueSource ? watch(props.valueSource) : null);
  const inputBackground = background ? background : null;

  const [imageFile, setImageFile] = useState(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [croppedImageFile, setCroppedImageFile] = useState(null);

  const sx = {
    root: {},
    label: {
      width: '100%',
      height: '100%',
      '&>.MuiTypography-root': {
        fontWeight: 'bold',
        color: 'info.800',
      },
    },
    inputContainer: {
      alignSelf: isMobile ? 'center' : null,
      position: 'relative',
      height: '200px',
      width: '200px',
      borderRadius: '50%',
      background: inputBackground
        ? `no-repeat center/100% url(${inputBackground})`
        : props.inputBackground ||
          'linear-gradient(60deg, rgba(2,0,36,1) 0%, rgba(73,9,121,1) 61%, rgba(60,0,177,1) 100%)',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      '&:after':
        !inputBackground && !props.inputText
          ? {
              content: '""',
              display: 'block',
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              background: 'rgba(0,0,0,.4)',
              borderRadius: '50%',
            }
          : null,
    },
  };

  const files = value ? (Array.isArray(value) ? value : [value]) : [];
  const onDrop = (newFiles, rejectedFiles, event) => {
    const updatedFiles = multiple ? [...files, ...newFiles] : [...newFiles];

    if (multiple) {
      setImageFile(composeFile(updatedFiles));
    } else {
      setImageFile(composeFile(updatedFiles[0]));
    }

    if (onDropProp) {
      onDropProp(newFiles, rejectedFiles, event);
    }
  };

  function composeFile(input) {
    let file = input?.rawFile ? input?.rawFile : input;
    let name;
    if (!file.name && imageFile?.rawFile) {
      name = uniqueId() + imageFile.rawFile.name;
      file = new File([file], name, { type: imageFile.rawFile.type });
    }
    name = name ? name : uniqueId() + file.name;
    const src = window.URL.createObjectURL(file);
    const rawFile: File & { path?: string } = new File([file], name, {
      type: file.type,
    });
    rawFile.path = name;
    const finalResult = {
      rawFile: rawFile,
      src,
      title: name,
    };

    return finalResult;
  }

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    maxSize,
    minSize,
    multiple,
    ...options,
    onDrop,
  });

  async function onImageLoad(e) {
    const { width, height } = e.currentTarget;

    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: 'px',
          width: Math.min(width, height),
        },
        1,
        width,
        height
      ),
      width,
      height
    );

    setTimeout(async () => {
      await makeClientCrop(crop);
      setCrop(crop);
    }, 100);
  }

  async function makeClientCrop(targetCrop) {
    if (imageRef.current && targetCrop.width && targetCrop.height) {
      setCroppedImageFile(await getCroppedImg(imageRef.current, targetCrop));
    }
  }

  async function getCroppedImg(sourceImage, crop) {
    const canvas = document.createElement('canvas');
    const scaleX = sourceImage.naturalWidth / sourceImage.width;
    const scaleY = sourceImage.naturalHeight / sourceImage.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      sourceImage,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );
    try {
      const blob = await new Promise((resolve) => {
        canvas.toBlob((file) => {
          resolve(composeFile(file));
        }, imageFile?.type || 'image/jpeg');
      });
      return blob;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  return useInputWithPolicies(
    <Box sx={[sx.root, ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx])]}>
      <Labeled sx={sx.label} label={props.label || 'Choose photo'}>
        <Stack
          sx={{
            alignSelf: isMobile ? 'center' : null,
            width: '200px',
          }}
        >
          {!imageFile ? (
            <>
              <Stack sx={sx.inputContainer} {...getRootProps()}>
                {!inputBackground ? (
                  <>
                    <Stack alignItems="center" sx={{ zIndex: 1 }}>
                      {props.inputText || (
                        <>
                          <AddAPhoto sx={{ color: 'common.white', fontSize: 40 }} />
                          <Typography color="common.white" variant={'h5'}>
                            Upload Photo
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </>
                ) : null}
                <input
                  id={id}
                  name={id}
                  {...getInputProps({
                    ...inputPropsOptions,
                  })}
                />
              </Stack>
              <Stack pt={2} spacing={3} direction="row" justifyContent="flex-end">
                <LLButton
                  fullWidth
                  variant="outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setValue(props.source, null);
                    setValue(props.valueSource, null);
                    props?.onDelete();
                  }}
                  disabled={!inputBackground}
                >
                  Delete
                </LLButton>
                <LLButton fullWidth {...getRootProps()} isLoading={props.isLoading}>
                  Upload
                </LLButton>
              </Stack>
            </>
          ) : (
            <>
              <ReactCrop
                circularCrop
                aspect={1}
                crop={crop}
                onComplete={(crop) =>
                  imageRef.current
                    ? makeClientCrop(crop)
                    : enqueueSnackbar('The picture is not yet ready for cropping', {
                        variant: 'warning',
                      })
                }
                onChange={(cropData) => setCrop(cropData)}
              >
                <img
                  ref={imageRef}
                  width="100%"
                  src={imageFile?.src}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
              <Stack pt={2} spacing={3} direction="row" justifyContent="flex-end">
                <LLButton
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setImageFile(null);
                  }}
                >
                  Cancel
                </LLButton>
                <LLButton
                  fullWidth
                  onClick={() => {
                    onChange(croppedImageFile);
                    props?.onChange?.(croppedImageFile);
                    setImageFile(null);
                  }}
                >
                  Crop
                </LLButton>
              </Stack>
            </>
          )}
        </Stack>
      </Labeled>
    </Box>,
    props.source,
    props.level,
    props.ignorePolicies
  );
};

export default CommonImageInput;
